import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getAlterCodeTypesSchema } from '@/types/schemas/materials/getAlterCodeTypes';
import type {
  AlterCodeType,
  Assortment,
  AssortmentDetails,
  CompetitorMaterial,
  Material,
  MaterialAlterCode,
  MaterialAlterCodeForSalesDocument,
  MaterialType,
  MeasurementUnit,
} from '@/types/Materials';
import { getAssortmentsSchema } from '@/types/schemas/materials/getAssortments';
import { getAssortmentByIdSchema } from '@/types/schemas/materials/getAssortmentById';
import { getMaterialsWithConditionsSchema } from '@/types/schemas/materials/getMaterialsWithConditions';
import { getAssortmentDetailsSchema } from '@/types/schemas/materials/getAssortmentDetails';
import type { TableFilterData } from '@/types/Base';
import { getMaterialByIdSchema } from '@/types/schemas/materials/getMaterialById';
import { getMaterialTypesSchema } from '@/types/schemas/materials/getMaterialTypes';
import { getCompetitorMaterialsWithConditionsSchema } from '@/types/schemas/materials/getCompetitorMaterialsWithConditions';
import { getMeasurementUnitsSchema } from '@/types/schemas/materials/getMeasurementUnits';
import {
  getMaterialAlterCodesWithConditionsSchema,
  getMaterialAlterCodesForSalesDocumentSchema,
} from '@/types/schemas/materials/getMaterialAlterCodesWithConditions';
import type { Warehouse } from '@/types/Warehouse';
import { getWarehousesSchema } from '@/types/schemas/materials/getWarehouses';
import type { RangeDetail } from '@/types/Ranges';
import { getRangeDetailsByCustomerIdSchema } from '@/types/schemas/documents/getRangeDetailsByCustomerId';
import type { MaterialDC } from '@/types/MaterialDC';
import { getMaterialsDCSchema } from '@/types/schemas/materials/getMaterialsDCSchema';

export async function getAlterCodeTypes(): Promise<AlterCodeType[]> {
  const response = await axios.get('/v2/materials/alter-code-types');
  return validate(getAlterCodeTypesSchema, response.data.data);
}

export async function removeAlterCodeTypesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/materials/alter-code-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getMaterialsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  excludeIDs,
  groupItemId,
  includeDeleted = true,
  grouped,
  priceCatalogId,
  isActive = true,
}: TableFilterData & {
  groupItemId?: string;
  includeDeleted?: boolean;
  grouped?: boolean;
  priceCatalogId?: string;
}): Promise<{
  data: Material[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(`/v2/materials/search?take=${itemsPerPage}&skip=${skip}`, {
    searchText: query,
    sortColumn,
    sortAscending,
    excludeIDs,
    groupItemId,
    includeDeleted,
    grouped,
    priceCatalogId,
    isActive,
  });

  const valid = validate(getMaterialsWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function addOrEditAlterCodeType(alterCodeType: AlterCodeType) {
  const response = await axios.post('/v2/materials/alter-code-types', alterCodeType);

  if (response.status === 201) return true;
  return false;
}

export async function removeMaterialsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/materials/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getMaterialById(id: string): Promise<Material | null> {
  const response = await axios.get(`/v2/materials/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });

  if (response.status === 404) return null;

  const valid = validate(getMaterialByIdSchema, response.data);
  return valid;
}

export async function getMaterialBalanceById(
  materialId: string,
  warehouseId: string,
  enumWarehouse: number
): Promise<string | null> {
  const response = await axios.post(
    `/v2/materials/balance`,
    {
      materialId: materialId,
      warehouseId: warehouseId,
      enumWarehouse: enumWarehouse,
    },
    {
      validateStatus: (status: number) => status === 400 || status === 404 || status == 200,
    }
  );

  if (response.status === 404) return null;

  return response.data;
}

// Assortments
export async function getAssortments() {
  const response = await axios.get('/v2/materials/assortments');
  return validate(getAssortmentsSchema, response.data.data);
}

export async function getAssortmentById(id: string) {
  const response = await axios.get(`/v2/materials/assortments/${id}`);
  return validate(getAssortmentByIdSchema, response.data);
}

export async function removeAssortmentsByIds(ids: string[]) {
  const response: any = await axios.post('/v2/materials/assortments/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditAssortment(assortment: Assortment) {
  const response: any = await axios.post('/v2/materials/assortments', assortment);

  if (response.status === 201) return true;
  return false;
}

export async function getAssortmentsDetails(assortmentId: string) {
  const response = await axios.get(`/v2/materials/assortment-details/${assortmentId}`);
  return validate(getAssortmentDetailsSchema, response.data.data);
}

export async function removeAssortmentDetailsByIds(ids: string[]) {
  const response: any = await axios.post('/v2/materials/assortment-details/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditAssortmentDetails(assortmentDetail: AssortmentDetails) {
  const response: any = await axios.post('/v2/materials/assortment-details', assortmentDetail);
  if (response.status === 201) return true;
  return false;
}

export async function getMaterialTypes(): Promise<MaterialType[]> {
  const response = await axios.get('v2/materials/material-types');

  return validate(getMaterialTypesSchema, response.data.data);
}

export async function addOrEditMaterial(material: Material) {
  const response = await axios.post('v2/materials', material);

  if (response.status === 201) return true;
  return false;
}

export async function getCompetitorMaterialsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  materialId = null,
  competitorId = null,
}: TableFilterData & { materialId?: string | null; competitorId?: string | null }): Promise<{
  data: CompetitorMaterial[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  let url = `/v2/materials/competitor-materials/search?take=${itemsPerPage}&skip=${skip}`;
  if (materialId != null) url += `&materialId=${materialId}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    competitorId,
  });

  const valid = validate(getCompetitorMaterialsWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function removeCompetitorMaterialsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/materials/competitor-materials/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getMeasurementUnits(): Promise<MeasurementUnit[]> {
  const response = await axios.get('v2/materials/measurement-units');

  return validate(getMeasurementUnitsSchema, response.data.data);
}

export async function getMaterialAlterCodesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  materialId = null,
  pricingPolicyId = null,
  rangeId = null,
  groupItemIds = [],
}: TableFilterData & {
  materialId?: string | null;
  pricingPolicyId?: string | null;
  rangeId?: string | null;
  groupItemIds?: string[];
}): Promise<{
  data: MaterialAlterCode[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const url = `v2/materials/alter-codes/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    materialId,
    pricingPolicyId,
    rangeId,
    groupItemIds,
  });

  const valid = validate(getMaterialAlterCodesWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getMaterialAlterCodesForSalesDocuments({
  query = '',
  sortColumn = 'material.id',
  sortAscending = true,
  materialAlterCodeId = null,
  pricingPolicyId = null,
}: {
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  materialAlterCodeId?: string | null;
  pricingPolicyId?: string | null;
}): Promise<MaterialAlterCodeForSalesDocument[]> {
  const url = `v2/materials/alter-codes/search-for-sales-documents`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    materialAlterCodeId,
    pricingPolicyId,
    rangeId: null,
  });

  const valid = validate(getMaterialAlterCodesForSalesDocumentSchema, response.data.data);
  return valid;
}

export async function addOrUpdateCompetitorMaterials(
  competitorMaterials: CompetitorMaterial[]
): Promise<boolean> {
  const response = await axios.post('v2/materials/competitor-materials', competitorMaterials);

  if (response.status === 201) return true;
  return false;
}

export async function getWarehouses(): Promise<Warehouse[]> {
  const response = await axios.get('/v2/materials/warehouses');
  return validate(getWarehousesSchema, response.data.data);
}

export async function addOrEditWarehouse(item: Warehouse) {
  const response = await axios.post('/v2/materials/warehouses', item);
  if (response.status === 201) return true;
  return false;
}

export async function removeWarehousesByIds(ids: string[]) {
  const response = await axios.post('/v2/materials/warehouses/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getRangeDetailsByCustomerTraderSite(
  documentTypeId: string,
  customerId: string,
  traderSiteId: string
): Promise<RangeDetail[] | null> {
  const response = await axios.get(
    `/v2/materials/${documentTypeId}/${customerId}/${traderSiteId}/range-details`,
    {
      validateStatus: (status: number) => status === 404 || status == 200,
    }
  );
  if (response.status === 404) return null;
  return validate(getRangeDetailsByCustomerIdSchema, response.data);
}

// Materials DC

export async function getMaterialsDCWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  distributionChannelId = undefined,
}: TableFilterData & { distributionChannelId?: string }): Promise<{
  data: MaterialDC[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  const url = `/v2/materials/materials-dc/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    distributionChannelId,
  });

  const valid = validate(getMaterialsDCSchema, response.data.data);
  return { data: valid as MaterialDC[], count: response.data.count };
}
